import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTrackEventMutation } from 'src/common/slices';
import { StyledSection, StyledSectionAccordionList, StyledSectionHeader, StyledSectionTitle } from '../post-room-completion-feedback-step.styles';
import { LoadingSection } from './loading-section';
interface FeedbackSectionProps<T> {
  title: string;
  items: T[];
  isLoading: boolean;
  skeletonCount: number;
  renderItem: (item: T, index: number, isOpen: boolean, onToggle: () => void) => ReactNode;
  type: 'good-commands' | 'to-improve';
  roomCode: string;
}
export const FeedbackSection = <T,>({
  title,
  items,
  isLoading,
  skeletonCount,
  renderItem,
  type,
  roomCode
}: FeedbackSectionProps<T>) => {
  const [trackEvent] = useTrackEventMutation();
  const [openAccordion, setOpenAccordion] = useState<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const toggleAccordion = useCallback((index: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    const isOpening = openAccordion !== index;
    const action = isOpening ? 'expand' : 'collapse';
    if (items[index]) {
      trackEvent({
        event: 'post-room-completion-feedback-accordion-toggle',
        properties: {
          type,
          index,
          action,
          room_code: roomCode
        }
      });
    }
    if (openAccordion === index) {
      setOpenAccordion(-1);
      return;
    }
    if (openAccordion !== -1) {
      setOpenAccordion(-1);
      timeoutRef.current = setTimeout(() => {
        setOpenAccordion(index);
      }, 300);
    } else {
      setOpenAccordion(index);
    }
  }, [openAccordion, items, trackEvent, type, roomCode]);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  if (isLoading) {
    return <LoadingSection skeletonCount={skeletonCount} />;
  }
  return <StyledSection data-sentry-element="StyledSection" data-sentry-component="FeedbackSection" data-sentry-source-file="feedback-section.tsx">
      <StyledSectionHeader data-sentry-element="StyledSectionHeader" data-sentry-source-file="feedback-section.tsx">
        <StyledSectionTitle data-sentry-element="StyledSectionTitle" data-sentry-source-file="feedback-section.tsx">{title}</StyledSectionTitle>
      </StyledSectionHeader>

      <StyledSectionAccordionList data-sentry-element="StyledSectionAccordionList" data-sentry-source-file="feedback-section.tsx">
        {items.map((item, index) => renderItem(item, index, openAccordion === index, () => toggleAccordion(index)))}
      </StyledSectionAccordionList>
    </StyledSection>;
};